// [NEW FILE] Import rows from Excel (.xlsx) or CSV for pages whose records the browser can create one by one.
// Steps: 1. download the template, 2. choose the filled file, 3. check the preview (each row valid or with errors),
// 4. import the valid rows. Rows are created one at a time with the page's own create call, so every row goes through
// the same backend checks and audit trail as when it is added by hand. A failed row does not stop the others.
//
// <RowImportModal
//   open title="Import goals"
//   columns={[{ key: "title", label: "Goal title", required: true, example: "Close 20 tickets a week", hint: "..." }]}
//   validate={(row, index, allRows) => ({ value, errors: [] })}   // row: { key: text } from the file
//   importRow={async (value) => ...}                               // throws on failure
//   onClose onDone />
import { useState } from "react";
import { FiDownload, FiUpload, FiCheck } from "react-icons/fi";
import Modal from "../common/Modal";
import Button from "../common/Button";
import Banner from "../common/Banner";
import DataTable from "../common/DataTable";
import ProgressBar from "../common/ProgressBar";
import { readSpreadsheetFile } from "../../utils/readSpreadsheet";
import { downloadXlsx } from "../../utils/exportExcel";
import { downloadCsv } from "../../utils/exportCsv";
import { getErrorMessage } from "../../utils/errors";

const norm = (text) => String(text || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

// File headers may be the column key ("title") or its label ("Goal title"); both are accepted, any case.
function mapRow(raw, columns) {
  const byHeader = Object.fromEntries(Object.entries(raw).map(([k, v]) => [norm(k), v]));
  return Object.fromEntries(columns.map((c) => {
    const v = byHeader[norm(c.label)] ?? byHeader[norm(c.key)];
    return [c.key, v === undefined || v === null ? "" : String(v).trim()];
  }));
}

export default function RowImportModal({ open, title, subtitle, columns, validate, importRow, onClose, onDone, templateName = "import-template", note }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [fileError, setFileError] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setRows([]); setFileName(""); setFileError(""); setProgress(null); setResult(null); };
  const close = () => { if (busy) return; reset(); onClose(); };

  const template = (format) => {
    const sample = Object.fromEntries(columns.map((c) => [c.key, c.example ?? ""]));
    const cols = columns.map((c) => ({ key: c.key, label: c.label }));
    if (format === "csv") downloadCsv(templateName, cols, [sample]);
    else downloadXlsx(templateName, [{ name: "Import", columns: cols, rows: [sample] }]);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    reset();
    setFileName(file.name);
    try {
      const data = (await readSpreadsheetFile(file)).map((raw) => mapRow(raw, columns))
        .filter((r) => Object.values(r).some((v) => v !== ""));
      if (!data.length) { setFileError("The file has no rows under the header."); return; }
      if (data.length > 1000) { setFileError("Import at most 1000 rows at a time."); return; }
      const checked = data.map((raw, i) => {
        const missing = columns.filter((c) => c.required && !raw[c.key]).map((c) => `${c.label} is required.`);
        const out = missing.length ? { value: null, errors: missing } : validate(raw, i, data) || { value: raw, errors: [] };
        return { line: i + 2, raw, value: out.value, errors: out.errors || [] };
      });
      setRows(checked);
    } catch (err) {
      setFileError(getErrorMessage(err));
    }
  };

  const valid = rows.filter((r) => !r.errors.length);
  const run = async () => {
    setBusy(true);
    const done = { created: 0, failed: [] };
    setProgress({ done: 0, total: valid.length });
    for (const r of valid) {
      try {
        await importRow(r.value, r.raw);
        done.created += 1;
      } catch (err) {
        done.failed.push(`Row ${r.line}: ${getErrorMessage(err)}`);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setBusy(false);
    setResult(done);
    if (done.created) onDone?.();
  };

  const previewColumns = [
    { key: "line", label: "Row", width: 60 },
    ...columns.slice(0, 4).map((c) => ({ key: c.key, label: c.label, render: (r) => r.raw[c.key] || "—", exportValue: (r) => r.raw[c.key] })),
    {
      key: "check", label: "Check", exportValue: (r) => (r.errors.length ? r.errors.join(" ") : "OK"),
      render: (r) => (r.errors.length ? <span style={{ color: "var(--pms-danger)" }}>{r.errors.join(" ")}</span> : <span style={{ color: "var(--pms-success)" }}>OK</span>),
    },
  ];

  return (
    <Modal
      open={open}
      size="lg"
      title={title}
      subtitle={subtitle || "Download the template, fill one row per record, then upload it. Rows with errors are skipped."}
      onClose={busy ? undefined : close}
      footer={result ? <Button onClick={close}>Done</Button> : (
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>Cancel</Button>
          <Button icon={FiCheck} onClick={run} loading={busy} disabled={!valid.length}>Import {valid.length || ""} {valid.length === 1 ? "row" : "rows"}</Button>
        </>
      )}
    >
      {!result && (
        <div className="form-stack">
          <div className="row-actions">
            <Button variant="secondary" icon={FiDownload} onClick={() => template("xlsx")}>Template (Excel)</Button>
            <Button variant="ghost" icon={FiDownload} onClick={() => template("csv")}>Template (CSV)</Button>
            <label className="btn btn-primary" style={{ cursor: busy ? "not-allowed" : "pointer" }}>
              <FiUpload size={15} aria-hidden="true" /> <span>Choose file</span>
              <input type="file" accept=".xlsx,.csv" hidden disabled={busy} onChange={onFile} />
            </label>
            {fileName && <span className="muted">{fileName}</span>}
          </div>
          <details className="import-columns">
            <summary>Columns in the template</summary>
            <ul>
              {columns.map((c) => <li key={c.key}><strong>{c.label}</strong>{c.required ? " (required)" : " (optional)"}{c.hint ? `: ${c.hint}` : ""}</li>)}
            </ul>
          </details>
          {note && <Banner tone="info" title="Good to know">{note}</Banner>}
          {fileError && <Banner tone="danger" title="Can't read the file">{fileError}</Banner>}
          {rows.length > 0 && (
            <>
              <p className="muted">{rows.length} rows read: {valid.length} ready, {rows.length - valid.length} with errors.</p>
              <DataTable dense keepOrder rowKey="line" rows={rows} columns={previewColumns} pageSize={8} exportName="Import check" />
            </>
          )}
          {progress && (
            <>
              <ProgressBar value={progress.total ? (progress.done / progress.total) * 100 : 100} label="Import progress" />
              <p className="muted">Importing {progress.done} of {progress.total}</p>
            </>
          )}
        </div>
      )}
      {result && (
        <Banner tone={result.failed.length ? "warning" : "success"} title={`${result.created} ${result.created === 1 ? "row" : "rows"} imported`}>
          {result.failed.length > 0 && <div>{result.failed.length} failed: {result.failed.slice(0, 10).join(" ")}{result.failed.length > 10 ? " …" : ""}</div>}
        </Banner>
      )}
    </Modal>
  );
}
